import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useStickyObserver} from '@github-ui/use-sticky-header/useStickyObserver'
import {UnderlinePanels} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {graphql, useFragment} from 'react-relay'

import {TEST_IDS} from '../../constants/test-ids'
import type {TabbedTimelineConfig} from '../OptionConfig'
import type {TabbedTimelineFragment$key} from './__generated__/TabbedTimelineFragment.graphql'
import type {EventProps} from './IssueTimelineItem'
import styles from './TabbedTimeline.module.css'
import {TimelinePanel} from './TimelinePanel'
import {useConversationTimelineItems} from './use-conversation-timeline-items'

type TabbedTimelineProps = {
  data: TabbedTimelineFragment$key
  highlightedEvent: string | undefined
  page?: number
  isTransferInProgress?: boolean
  showCopilotMenuItem?: boolean
  optionConfig: EventProps['optionConfig'] & TabbedTimelineConfig
} & Omit<EventProps, 'optionConfig'>

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const tabbedTimelineFragment = graphql`
  fragment TabbedTimelineFragment on Issue @argumentDefinitions(itemTypes: {type: "[IssueTimelineItemsItemType!]"}) {
    id
    url
    repository {
      id
      nameWithOwner
      visibility
      viewerCanPush
    }
    ...useConversationTimelineItemsFrontFragment @arguments(itemTypes: $itemTypes)
    ...useConversationTimelineItemsBackFragment @arguments(itemTypes: $itemTypes)
  }
`

/**
 * Tabbed timeline view that splits events into "Conversation" and "Activity" tabs.
 *
 * Currently uses the classic (unfiltered) timeline data for the Conversation tab.
 * The Activity tab is a placeholder — filtered data will be wired in once the
 * query-level changes are in place.
 *
 * Enabled when `tabbedTimeline` is `true` in the option config (driven by
 * the `issues_react_timeline_tabs` feature flag on the server side).
 */
export function TabbedTimeline({
  data,
  highlightedEvent,
  page,
  isTransferInProgress,
  showCopilotMenuItem,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: TabbedTimelineProps) {
  const timelinePaginationForBotsEnabled = isFeatureEnabled('issues_react_bots_timeline_pagination')
  const effectivePage = timelinePaginationForBotsEnabled ? page : undefined
  const timelineData = useFragment<TabbedTimelineFragment$key>(tabbedTimelineFragment, data)
  // ─── Conversation tab ──────────────────────────────────────────────────

  const {timelineItems, totalItemCount, highlightedReady} = useConversationTimelineItems({
    timelineData,
    itemTypes: optionConfig.conversationEvents,
    highlightedEvent,
    page: effectivePage,
  })

  const {isSticky, sentinelRef} = useStickyObserver()

  return (
    <>
      <div ref={sentinelRef} className={styles.sentinel} />
      <UnderlinePanels
        aria-label="Timeline"
        className={clsx(styles.tabbedNav, isSticky && styles.stuck)}
        data-testid={TEST_IDS.tabbedTimelineNav}
      >
        <UnderlinePanels.Tab aria-selected counter={totalItemCount}>
          Conversation
        </UnderlinePanels.Tab>
        <UnderlinePanels.Tab>Activity</UnderlinePanels.Tab>

        <UnderlinePanels.Panel data-testid={TEST_IDS.tabbedTimelineConversationPanel}>
          <TimelinePanel
            timelineItems={timelineItems}
            totalItemCount={totalItemCount}
            highlightedEvent={highlightedEvent}
            highlightedReady={highlightedReady}
            issueId={timelineData.id}
            repositoryId={timelineData.repository.id}
            repositoryNameWithOwner={timelineData.repository.nameWithOwner}
            issueUrl={timelineData.url}
            page={effectivePage}
            isTransferInProgress={isTransferInProgress}
            showCopilotMenuItem={showCopilotMenuItem}
            viewer={viewer}
            onCommentChange={onCommentChange}
            onCommentReply={onCommentReply}
            onCommentEditCancel={onCommentEditCancel}
            optionConfig={optionConfig}
            disableLiveUpdates
          />
        </UnderlinePanels.Panel>

        <UnderlinePanels.Panel data-testid={TEST_IDS.tabbedTimelineActivityPanel}>
          {/* Activity panel — placeholder until filtered data is available */}
        </UnderlinePanels.Panel>
      </UnderlinePanels>
    </>
  )
}
